import { useEffect, useMemo, useRef, useState } from 'react';
import { LuGripHorizontal, LuTrash2, LuDownload } from 'react-icons/lu';
import type { BlocoPainel } from '../services/RelatorioPersonalizadoService';

/* A grade do painel: 12 colunas, linhas de 88px, arrastar e redimensionar.

   Sem dependência nova. O projeto tem @hello-pangea/dnd, mas ele resolve lista
   ordenável, não grade com redimensionamento; a mecânica aqui é a mesma do
   canvas de Trilhos — onPointerDown + listeners no window — com encaixe na
   grade.

   Duas regras que o desenho pediu e que valem ler antes de mexer:

   1. Nada se sobrepõe. Dois blocos no mesmo lugar escondem dado, e um painel
      que esconde dado mente. Quem chega empurra quem estava, em cascata.
   2. Nada flutua. Depois de cada mudança a grade é compactada para cima, então
      não sobra buraco entre um bloco e o topo. É o que torna o arranjo
      previsível: o mesmo gesto dá sempre o mesmo resultado.

   Abaixo de 1100px a grade livre não faz sentido (um bloco de 3 colunas teria
   240px) — vira uma coluna só, na ordem de leitura, e sem arrastar.          */

export const COLUNAS = 12;
export const LINHA = 88;
export const GAP = 12;
/** Abaixo disto um bloco de 3 colunas não caberia sem quebrar o próprio rótulo. */
export const LARGURA_MINIMA_BLOCO = 3;
const ESTREITO = 1100;

type Caixa = Pick<BlocoPainel, 'id' | 'x' | 'y' | 'w' | 'h'>;

const colide = (a: Caixa, b: Caixa): boolean =>
    a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

/** Puxa todo mundo para cima até encostar — sem buraco entre o bloco e o topo. */
function compactar<T extends Caixa>(blocos: T[]): T[] {
    const ordem = [...blocos].sort((a, b) => a.y - b.y || a.x - b.x);
    const postos: T[] = [];

    for (const bloco of ordem) {
        let y = bloco.y;
        while (y > 0 && !postos.some(p => colide({ ...bloco, y: y - 1 }, p))) y--;
        postos.push({ ...bloco, y });
    }

    return postos;
}

/**
 * Acomoda a grade depois de mover ou redimensionar `prioridade`: quem colidir
 * com ele desce, em cascata, e no fim tudo sobe de volta até encostar.
 */
export function acomodar<T extends Caixa>(blocos: T[], prioridade: string): T[] {
    const res = blocos.map(b => ({ ...b }));
    const alvo = res.find(b => b.id === prioridade);
    if (!alvo) return compactar(res);

    const empurrar = (fixo: Caixa, jaMovidos: Set<string>) => {
        for (const outro of res) {
            if (jaMovidos.has(outro.id)) continue;
            if (!colide(fixo, outro)) continue;
            outro.y = fixo.y + fixo.h;
            jaMovidos.add(outro.id);
            empurrar(outro, jaMovidos);
        }
    };

    empurrar(alvo, new Set([alvo.id]));
    return compactar(res);
}

/** Onde nasce o próximo bloco: embaixo de tudo, à esquerda. */
export function proximaVaga(blocos: Caixa[]): { x: number; y: number } {
    return { x: 0, y: blocos.reduce((max, b) => Math.max(max, b.y + b.h), 0) };
}

interface Arrasto {
    id: string;
    tipo: 'mover' | 'redimensionar';
    /** Ponto do ponteiro no início, em px. */
    px: number;
    py: number;
    /** Caixa do bloco no início, em células. */
    base: Caixa;
    /** Deslocamento atual do ponteiro, em px. */
    dx: number;
    dy: number;
}

interface Props {
    blocos: BlocoPainel[];
    editando: boolean;
    selecionado: string | null;
    onSelecionar: (id: string) => void;
    /** Só em edição: a grade devolve o arranjo inteiro já acomodado. */
    onArranjar: (blocos: BlocoPainel[]) => void;
    onRemover: (id: string) => void;
    /**
     * Baixar o bloco em CSV. Só em leitura, e no MESMO lugar onde a lixeira fica
     * em edição — um ícone por bloco, sempre visível. Aparecer só ao passar o
     * mouse esconderia que a exportação existe, e a casa não muda nada no hover.
     * `null` quando o bloco ainda não tem resultado: baixar arquivo vazio é pior
     * que o botão apagado.
     */
    onExportar?: (id: string) => void | null;
    /** Quais blocos já têm resultado para exportar. */
    podeExportar?: (id: string) => boolean;
    /** O corpo do bloco — resultado, erro ou esqueleto; quem sabe disso é a página. */
    render: (bloco: BlocoPainel) => React.ReactNode;
    /** Rodapé opcional do bloco (ex.: o rótulo de fonte em leitura). */
    rodape?: (bloco: BlocoPainel) => React.ReactNode;
}

export default function PainelGrade({
    blocos, editando, selecionado, onSelecionar, onArranjar, onRemover, onExportar, podeExportar, render, rodape,
}: Props) {
    const ref = useRef<HTMLDivElement | null>(null);
    const [largura, setLargura] = useState(0);
    const [arrasto, setArrasto] = useState<Arrasto | null>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        /* `clientWidth`, e não `contentRect.width`: quando o painel é mais alto que
           a tela a coluna ganha barra de rolagem, e o contentRect ainda conta a
           faixa dela — os blocos ficavam 2px largos demais e a borda direita do
           último saía cortada. `clientWidth` já desconta a barra. */
        const obs = new ResizeObserver(() => setLargura(el.clientWidth));
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    /* "Estreito" é a TELA, não este container.
       Medido no container, abrir o editor de 340px já derrubava a grade para uma
       coluna em 1440px — e com ela sumiam a alça e o canto, ou seja, abrir o
       editor desligava o arrastar. A largura do container continua valendo para
       a matemática das células; quem decide o modo é a janela. */
    const [estreito, setEstreito] = useState(() => window.innerWidth < ESTREITO);

    useEffect(() => {
        const consulta = window.matchMedia(`(max-width: ${ESTREITO - 1}px)`);
        const ouvir = () => setEstreito(consulta.matches);
        ouvir();
        consulta.addEventListener('change', ouvir);
        return () => consulta.removeEventListener('change', ouvir);
    }, []);
    /* O 1px descontado é a borda do último bloco: sem ele a soma de 12 colunas
       fecha exatamente na largura do container e a borda direita cai meio pixel
       fora, aparecendo cortada (medido: bloco em 1419, área útil até 1418,4). */
    const larguraColuna = largura > 0 ? (largura - 1 - GAP * (COLUNAS - 1)) / COLUNAS : 0;

    /* O arranjo que a tela mostra AGORA: durante o arrasto é a prévia acomodada,
       para os vizinhos já saírem da frente enquanto o bloco ainda está na mão. */
    const arranjo = useMemo(() => {
        if (!arrasto || larguraColuna === 0) return blocos;

        const celulaX = (px: number) => Math.round(px / (larguraColuna + GAP));
        const celulaY = (px: number) => Math.round(px / (LINHA + GAP));

        const { id, tipo, base, dx, dy } = arrasto;
        const proposto: BlocoPainel[] = blocos.map(b => {
            if (b.id !== id) return b;
            if (tipo === 'mover') {
                return {
                    ...b,
                    x: Math.max(0, Math.min(COLUNAS - base.w, base.x + celulaX(dx))),
                    y: Math.max(0, base.y + celulaY(dy)),
                };
            }
            return {
                ...b,
                w: Math.max(LARGURA_MINIMA_BLOCO, Math.min(COLUNAS - base.x, base.w + celulaX(dx))),
                h: Math.max(1, base.h + celulaY(dy)),
            };
        });

        return acomodar(proposto, id);
    }, [blocos, arrasto, larguraColuna]);

    const linhas = arranjo.reduce((max, b) => Math.max(max, b.y + b.h), 0);

    /* Os listeners ficam no window, e não no bloco: soltar o ponteiro fora da
       grade (ou fora da janela) tem que terminar o arrasto do mesmo jeito —
       senão o bloco fica grudado no cursor. */
    useEffect(() => {
        if (!arrasto) return;

        const mover = (e: PointerEvent) =>
            setArrasto(a => (a ? { ...a, dx: e.clientX - a.px, dy: e.clientY - a.py } : a));

        const soltar = () => {
            setArrasto(null);
            onArranjar(arranjo);
        };

        window.addEventListener('pointermove', mover);
        window.addEventListener('pointerup', soltar);
        window.addEventListener('pointercancel', soltar);
        return () => {
            window.removeEventListener('pointermove', mover);
            window.removeEventListener('pointerup', soltar);
            window.removeEventListener('pointercancel', soltar);
        };
    }, [arrasto, arranjo, onArranjar]);

    const pegar = (e: React.PointerEvent, bloco: BlocoPainel, tipo: Arrasto['tipo']) => {
        if (!editando || estreito) return;
        e.preventDefault();
        e.stopPropagation();
        onSelecionar(bloco.id);
        setArrasto({
            id: bloco.id, tipo,
            px: e.clientX, py: e.clientY,
            base: { id: bloco.id, x: bloco.x, y: bloco.y, w: bloco.w, h: bloco.h },
            dx: 0, dy: 0,
        });
    };

    const estilo = (b: BlocoPainel): React.CSSProperties => {
        if (estreito) return {};
        const caixa: React.CSSProperties = {
            left: b.x * (larguraColuna + GAP),
            top: b.y * (LINHA + GAP),
            width: b.w * larguraColuna + (b.w - 1) * GAP,
            height: b.h * LINHA + (b.h - 1) * GAP,
        };
        /* O bloco na mão sai do encaixe e segue o ponteiro em pixels: sem isso o
           arrasto anda de célula em célula e parece travado. */
        if (arrasto?.id === b.id && arrasto.tipo === 'mover') {
            caixa.transform = `translate(${arrasto.dx}px, ${arrasto.dy}px)`;
        }
        return caixa;
    };

    const ordemEstreita = useMemo(
        () => [...arranjo].sort((a, b) => a.y - b.y || a.x - b.x),
        [arranjo],
    );

    const lista = estreito ? ordemEstreita : arranjo;

    return (
        <div
            ref={ref}
            className={`rl-grade ${estreito ? 'is-estreita' : ''} ${editando ? 'is-edit' : ''} ${arrasto ? 'is-arrastando' : ''}`}
            style={estreito || largura === 0 ? undefined : { height: linhas * (LINHA + GAP) - GAP }}
        >
            {lista.map(b => (
                <section
                    key={b.id}
                    className={[
                        'rl-bloco',
                        selecionado === b.id && editando ? 'is-sel' : '',
                        arrasto?.id === b.id ? 'is-mao' : '',
                    ].join(' ')}
                    style={estreito ? { minHeight: b.h * LINHA } : estilo(b)}
                    onClick={() => editando && onSelecionar(b.id)}
                >
                    <header className="rl-bloco-h">
                        {editando && !estreito && (
                            <button
                                type="button"
                                className="rl-alca"
                                aria-label={`Mover ${b.titulo ?? 'bloco'}`}
                                onPointerDown={e => pegar(e, b, 'mover')}
                            >
                                <LuGripHorizontal size={13} />
                            </button>
                        )}
                        <span className="rl-bloco-t">{b.titulo || 'Sem título'}</span>
                        {editando ? (
                            <button
                                type="button"
                                className="rl-bloco-x"
                                aria-label={`Remover ${b.titulo ?? 'bloco'}`}
                                onClick={e => { e.stopPropagation(); onRemover(b.id); }}
                            >
                                <LuTrash2 size={13} />
                            </button>
                        ) : onExportar && (
                            <button
                                type="button"
                                className="rl-bloco-x"
                                aria-label={`Baixar ${b.titulo ?? 'bloco'} em CSV`}
                                title="Baixar em CSV"
                                disabled={podeExportar ? !podeExportar(b.id) : false}
                                onClick={e => { e.stopPropagation(); onExportar(b.id); }}
                            >
                                <LuDownload size={13} />
                            </button>
                        )}
                    </header>

                    <div className="rl-bloco-b">{render(b)}</div>

                    {rodape?.(b)}

                    {editando && !estreito && (
                        <span
                            className="rl-canto"
                            role="separator"
                            aria-label={`Redimensionar ${b.titulo ?? 'bloco'}`}
                            onPointerDown={e => pegar(e, b, 'redimensionar')}
                        />
                    )}
                </section>
            ))}
        </div>
    );
}
